import { useEffect, useState } from 'react';
import { formatMoney, parseAmount } from '@domain';
import { loadCreditOptions, type CreditDetail, type CreditOption, type DataSource } from '../api.js';
import type { Translate } from '../i18n/index.js';
import { SearchableSelect, type SelectOption } from '../components/SearchableSelect.js';
import { theme } from '../theme.js';
import { Card } from '../components/ui.js';

/**
 * Money given back on this course.
 *
 * WHY THIS IS A SEPARATE PANEL FROM THE COSTS
 * A refund is not something the course spent. Putting it among the costs
 * would reach the right bottom line through two wrong figures, and the
 * cost side gets quoted on its own.
 *
 * WHY ANYTHING HAS TO BE LINKED AT ALL
 * A credit note raised against an unpaid invoice needs nothing: Xero
 * records it on that invoice and the cohort nets it off by itself. This
 * panel is for the other case — a student who paid in full and was
 * refunded afterwards. Xero has no balance left to allocate against, so
 * the credit note stands alone and nothing on it says which course it
 * belonged to.
 */

export function DeliveryCredits({
  deliveryId,
  credits,
  currency,
  locked,
  busy,
  source,
  t,
  onLink,
  onUnlink,
}: {
  deliveryId: number;
  credits: CreditDetail[];
  currency: string;
  locked: boolean;
  busy: boolean;
  source: DataSource;
  t: Translate;
  onLink: (credit: {
    xeroId: string;
    allocatedAmount: number | null;
    note: string | null;
  }) => void;
  onUnlink: (creditId: number) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [options, setOptions] = useState<CreditOption[]>([]);
  const [chosen, setChosen] = useState('');
  const [share, setShare] = useState('');

  useEffect(() => {
    if (!adding) return;
    let cancelled = false;
    loadCreditOptions(source)
      .then((loaded) => !cancelled && setOptions(loaded.credits))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [adding, source, deliveryId]);

  const picked = options.find((option) => option.xeroId === chosen) ?? null;

  const selectOptions: SelectOption[] = options.map((option) => ({
    value: option.xeroId,
    label: [
      option.date ?? '',
      option.contact || option.number,
      `${formatMoney(option.subTotal)} ${option.currencyCode || currency}`,
      option.usedElsewhere ? t('(already on another course)') : '',
    ]
      .filter(Boolean)
      .join('  ·  '),
  }));

  const cell = {
    padding: '9px 12px',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.dividerSoft}`,
  };

  const heading = {
    padding: '9px 12px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '.04em',
    color: theme.label,
    borderBottom: `1px solid ${theme.border}`,
    textAlign: 'start' as const,
  };

  /*
    Nothing linked and nothing to link. The panel stays out of the way
    rather than teaching a case most courses never meet — a cohort with
    no refunds should not have to read about refunds.
  */
  if (credits.length === 0 && locked) return null;

  return (
    <Card style={{ marginBottom: '14px' }}>
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${theme.dividerSoft}`,
          fontWeight: 600,
          fontSize: '13px',
          background: theme.surfaceHeader,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <span>{t('Money given back')}</span>
        {!locked && !adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            style={{
              background: 'transparent',
              border: `1px solid ${theme.borderStrong}`,
              borderRadius: theme.radiusControl,
              padding: '6px 13px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + {t('Put a credit note on this course')}
          </button>
        )}
      </div>

      {adding && (
        <div style={{ padding: '16px', borderBottom: `1px solid ${theme.dividerSoft}` }}>
          <label style={{ display: 'block', marginBottom: '12px' }}>
            <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' }}>
              {t('Credit note')}
            </span>
            <SearchableSelect
              value={chosen}
              options={selectOptions}
              placeholder={t('Search your credit notes…')}
              emptyLabel={t('No credit notes found in Xero')}
              onChange={setChosen}
              t={t}
            />
          </label>

          <label style={{ display: 'block', maxWidth: '220px', marginBottom: '14px' }}>
            <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' }}>
              {t('Share for this course')}{' '}
              <span style={{ fontWeight: 400, color: theme.textMuted }}>
                ({t('blank = all of it')})
              </span>
            </span>
            <input
              value={share}
              onChange={(e) => setShare(e.target.value)}
              inputMode="decimal"
              dir="ltr"
              placeholder={picked ? formatMoney(picked.subTotal) : ''}
              style={{
                height: '34px',
                width: '100%',
                border: `1px solid ${theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                padding: '0 10px',
                fontSize: '13px',
                background: '#fff',
                fontFamily: theme.fontMono,
              }}
            />
          </label>

          <div style={{ display: 'flex', gap: '9px' }}>
            <button
              type="button"
              disabled={picked === null || busy}
              onClick={() => {
                if (!picked) return;
                onLink({
                  xeroId: picked.xeroId,
                  allocatedAmount: share.trim() === '' ? null : parseAmount(share),
                  note: null,
                });
                setAdding(false);
                setChosen('');
                setShare('');
              }}
              style={{
                height: '36px',
                minWidth: '130px',
                background: picked && !busy ? theme.nav : theme.borderStrong,
                color: '#fff',
                border: 0,
                borderRadius: theme.radiusControl,
                fontSize: '13px',
                fontWeight: 600,
                cursor: picked && !busy ? 'pointer' : 'default',
              }}
            >
              {t('Add it')}
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                background: 'transparent',
                border: `1px solid ${theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      )}

      {credits.length === 0 ? (
        <div style={{ padding: '16px', fontSize: '13px', color: theme.textMuted, lineHeight: 1.6 }}>
          {t('Nothing has been given back on this course.')}{' '}
          {t('A credit note against an unpaid invoice is counted on its own — this is only for a student refunded after paying in full.')}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={heading}>{t('Credit note')}</th>
              <th style={heading}>{t('Who')}</th>
              <th style={heading}>{t('Date')}</th>
              <th style={{ ...heading, textAlign: 'end' }}>{t('Off this course')}</th>
              <th style={{ ...heading, width: '90px' }} />
            </tr>
          </thead>
          <tbody>
            {credits.map((credit) => (
              <tr key={credit.creditId} style={{ opacity: credit.missing ? 0.5 : 1 }}>
                <td style={cell}>
                  {credit.number || t('Credit note')}
                  {credit.missing && (
                    <div style={{ color: theme.warnDeep, fontSize: '11.5px', fontWeight: 600 }}>
                      {t('Xero no longer has this. It is not counted.')}
                    </div>
                  )}
                  {/*
                    The part Xero already put against one of this cohort's
                    own invoices is counted there. Saying so stops the
                    figure beside it looking wrong.
                  */}
                  {credit.allocatedToCohort > 0 && (
                    <div style={{ color: theme.textMuted, fontSize: '11.5px' }}>
                      {t('Part of it was already taken off an invoice here.')}
                    </div>
                  )}
                </td>
                <td style={{ ...cell, color: theme.textMuted }}>{credit.contact || '—'}</td>
                <td style={{ ...cell, fontFamily: theme.fontMono, fontSize: '12px' }} dir="ltr">
                  {credit.date ?? '—'}
                </td>
                <td
                  style={{ ...cell, textAlign: 'end', fontFamily: theme.fontMono, direction: 'ltr' }}
                >
                  {formatMoney(credit.allocatedAmount ?? credit.subTotal)}
                  {credit.currencyCode !== '' && credit.currencyCode !== currency && (
                    <div style={{ color: theme.warnDeep, fontSize: '11px', fontWeight: 600 }}>
                      {credit.currencyCode}
                    </div>
                  )}
                </td>
                <td style={{ ...cell, textAlign: 'end' }}>
                  {!locked && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onUnlink(credit.creditId)}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${theme.border}`,
                        borderRadius: theme.radiusControl,
                        padding: '5px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: busy ? 'default' : 'pointer',
                      }}
                    >
                      {t('Remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ padding: '11px 16px', fontSize: '11.5px', color: theme.textMuted, lineHeight: 1.6 }}>
        {t('This comes off what the course earned, not onto what it cost. Amounts exclude tax.')}
      </div>
    </Card>
  );
}
